import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { Users, Building2, ShieldCheck, CreditCard, FileCheck, ChevronLeft } from 'lucide-react';
import { DashboardCharts } from './dashboard-charts';
import { useTranslations } from '@/i18n/use-locale';
import { useLocalizedName } from '@/i18n/localized-name';
import { formatCount, useFormatSar } from '@/lib/format';
import { QueryGate } from '@/components/ui/query-gate';
import { useAnalyticsSummaryQuery, useDemandSupplyQuery, usePaymentsQuery } from '@/lib/queries';
import { KpiCard } from '@/components/admin/kpi-card';
import { Card } from '@/components/ui/card';

export function DashboardRoute() {
  const navigate = useNavigate();
  const t = useTranslations('admin');
  const sar = useFormatSar();
  const name = useLocalizedName();

  const summaryQuery = useAnalyticsSummaryQuery();
  const paymentsQuery = usePaymentsQuery();
  const demandQuery = useDemandSupplyQuery();

  // The summary is the page's spine — every KPI tile reads from it — so it gates
  // the whole route. Payments and demand/supply are secondary panels: a slow or
  // failed one degrades to "—"/empty rather than blocking the dashboard.
  return (
    <QueryGate query={summaryQuery}>
      {(summary) => {
        const revenueSAR = paymentsQuery.data
          ? sar(paymentsQuery.data.summary.grossPaidMinor / 100)
          : '—';
        // The design's "الطلب مقابل العرض" panel ranks roles by how much clubs ask for
        // them (top 8), not by shortage — the red bar is the design's own rule
        // (كوادر أقل من ٥), read off `supply`, not the backend `shortage` flag.
        const topDemandRoles = [...(demandQuery.data ?? [])]
          .sort((a, b) => b.demand - a.demand)
          .slice(0, 8);
        const maxDemand = topDemandRoles.reduce((m, r) => Math.max(m, r.demand), 0) || 1;
        const pendingDocs = summary.queues.documents;

        return (
          <div className="space-y-6">
            {/* 4 KPI Cards */}
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <KpiCard
                href="/subscriptions"
                label={t('kpi.subscribed')}
                value={formatCount(summary.subscriptions.active)}
                icon={Users}
                hint={t('dashboard.kpi.ofTalents', { count: formatCount(summary.talents.total) })}
              />
              <KpiCard
                href="/organizations"
                tone="navy"
                label={t('kpi.organizations')}
                value={formatCount(summary.organizations.total)}
                icon={Building2}
                hint={t('dashboard.kpi.verifiedOrgs', {
                  count: formatCount(summary.organizations.verified),
                })}
              />
              <KpiCard
                href="/verification"
                tone="gold"
                label={t('kpi.verifications')}
                value={formatCount(summary.queues.verifications)}
                icon={ShieldCheck}
                hint={t('dashboard.kpi.awaitingApproval')}
                emphasizeHint
              />
              <KpiCard
                href="/subscriptions"
                label={t('kpi.revenue')}
                value={revenueSAR}
                icon={CreditCard}
                hint={t('dashboard.kpi.revenueHint')}
              />
            </div>

            {/* Charts Section */}
            <DashboardCharts />

            {/* Demand vs Supply & Quality Stats Grid */}
            <div className="grid gap-6 lg:grid-cols-3">
              {/* Demand vs Supply Roles */}
              <Card className="p-5 lg:col-span-2">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-[15px] font-bold text-navy">
                      {t('charts.demandSupply.title')}
                    </h3>
                    <p className="text-[12.5px] text-conc-500">
                      {t('dashboard.demand.hint', { count: formatCount(5) })}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="link"
                    size="none"
                    onClick={() => void navigate('/roles')}
                    className="gap-1 text-[12.5px] font-bold"
                  >
                    {t('dashboard.demand.rolesLink')}
                    {/* Points forward: left in Arabic, right in English. */}
                    <ChevronLeft aria-hidden className="h-4 w-4 ltr:rotate-180" />
                  </Button>
                </div>

                <div className="space-y-3">
                  {topDemandRoles.map((role) => {
                    const low = role.supply < 5;
                    const width = Math.round((role.demand / maxDemand) * 100);
                    return (
                      <Button
                        key={role.specialtyId}
                        type="button"
                        variant="outline"
                        size="none"
                        onClick={() => void navigate('/roles')}
                        className="block w-full rounded-xl border-conc-100 p-3.5 text-start font-normal whitespace-normal hover:border-pitch hover:bg-sideline"
                      >
                        <div className="mb-1.5 flex items-center justify-between text-[13px]">
                          <span className="font-semibold text-navy">{name(role)}</span>
                          <span
                            className={`num text-[12px] font-semibold ${low ? 'text-rose-600' : 'text-conc-500'}`}
                          >
                            {t('dashboard.demand.counts', {
                              supply: formatCount(role.supply),
                              demand: formatCount(role.demand),
                            })}
                          </span>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-conc-100">
                          <div
                            className={`h-full rounded-full ${low ? 'bg-rose-500' : 'bg-pitch'}`}
                            style={{ width: `${Math.max(8, width)}%` }}
                          />
                        </div>
                      </Button>
                    );
                  })}
                  {topDemandRoles.length === 0 && (
                    <p className="py-8 text-center text-[13px] text-conc-400">
                      {t('dashboard.demand.empty')}
                    </p>
                  )}
                </div>
              </Card>

              {/* Quality / review queue */}
              <Card className="p-5">
                <div className="mb-4 flex items-center gap-3">
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-pitch-50 text-pitch">
                    <FileCheck className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="text-[15px] font-bold text-navy">
                      {t('dashboard.quality.title')}
                    </h3>
                    <p className="text-[12px] text-conc-500">{t('dashboard.quality.subtitle')}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="rounded-xl bg-sideline p-3.5">
                    <div className="flex justify-between text-[12.5px]">
                      <span className="font-semibold text-conc-600">
                        {t('dashboard.quality.pending')}
                      </span>
                      <span className="num font-bold text-gold-700">
                        {t('dashboard.quality.pendingCount', { count: formatCount(pendingDocs) })}
                      </span>
                    </div>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-conc-100">
                      <div
                        className="h-full rounded-full bg-gold"
                        style={{ width: `${Math.min(100, pendingDocs * 15)}%` }}
                      />
                    </div>
                  </div>

                  <Button
                    type="button"
                    onClick={() => void navigate('/documents')}
                    className="mt-2 w-full rounded-xl py-3"
                  >
                    {t('dashboard.quality.openQueue', { count: formatCount(pendingDocs) })}
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        );
      }}
    </QueryGate>
  );
}
